import React, { useState, useEffect } from 'react';
import { bool, func, object, number, string, shape, arrayOf, oneOf } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, intlShape } from '../../../../util/reactIntl';
import {
  AUTHENTICATION_PAGES,
  ACCOUNT_SETTINGS_PAGES,
} from '../../../../routing/routeConfiguration';
import { propTypes } from '../../../../util/types';
import { isUserAdmin } from '../../../../util/data';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
  NotificationBadge,
  IconMessages,
  IconCart,
  IconFolder,
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';

import css from './TopbarDesktop.module.css';

// Define pages where the custom links should be hidden
const shouldHideCustomLinks = currentPage => {
  const hiddenPages = ['ListingPage'];
  return hiddenPages.includes(currentPage);
};

const SignupLink = () => (
  <NamedLink name="SignupPage" className={css.topbarLink}>
    <span className={css.topbarLinkLabel}>
      <FormattedMessage id="TopbarDesktop.signup" />
    </span>
  </NamedLink>
);

const LoginLink = () => (
  <NamedLink name="LoginPage" className={css.topbarLink}>
    <span className={css.topbarLinkLabel}>
      <FormattedMessage id="TopbarDesktop.login" />
    </span>
  </NamedLink>
);

const CreateListingLink = () => (
  <NamedLink name="NewListingPage" className={classNames(css.topbarLink, css.createListingLink)}>
    <span className={css.topbarLinkLabel}>
      <FormattedMessage id="TopbarDesktop.createListingLink" />
    </span>
  </NamedLink>
);

const InboxLink = ({ notificationCount }) => (
  <NamedLink
    name="InboxPage"
    className={classNames(css.topbarLink, css.inboxLink)}
    params={{ tab: 'orders' }}
  >
    <IconMessages className={css.inboxIcon} />
    <span className={css.topbarLinkLabel}>
      <FormattedMessage id="TopbarDesktop.inbox" />
    </span>
    {notificationCount > 0 && (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    )}
  </NamedLink>
);

const CartLink = ({ currentUserCartItemsCount, hasOrderDraftItems, location }) => {
  const { pathname, search, hash } = location;
  const totalQuantity = currentUserCartItemsCount;

  const renderNotification = () => {
    if (totalQuantity > 0) {
      return <NotificationBadge className={css.cartNotificationBadge} count={totalQuantity} />;
    }
    if (totalQuantity === 0 && hasOrderDraftItems) {
      return <div className={css.notificationDot} />;
    }
    return null;
  };

  return (
    <NamedLink
      name="CartPage"
      className={css.topbarLinkWithIcon}
      to={{
        state: {
          from: `${pathname}${search}${hash}`,
        },
      }}
    >
      <IconCart className={css.topbarLinkIcon} />
      {renderNotification()}
    </NamedLink>
  );
};

const SearchLink = () => (
  <NamedLink name="SearchPage" className={css.topbarLinkWithIcon}>
    <IconFolder className={css.topbarLinkIcon} />
  </NamedLink>
);

const ProfileMenu = ({
  currentPage,
  currentUser,
  isCurrentUserAdmin,
  onLogout,
  notificationCount,
}) => {
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  const notificationDot =
    notificationCount.messageNotificationCount > 0 ||
      notificationCount.requestNotificationCount > 0 ? (
      <div className={css.notificationDot} />
    ) : null;

  return (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <div className={css.avatarWrapper}>
          <Avatar className={css.avatar} user={currentUser} disableProfileLink />
          {notificationDot}
        </div>
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        <MenuItem key="RequestsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('RequestsPage'))}
            name="RequestsPage"
            params={{ tab: isCurrentUserAdmin ? 'received' : 'sent' }}
          >
            <span className={css.menuItemBorder} />
            <span className={css.menuItemLabel}>
              <FormattedMessage id="TopbarDesktop.requestsLink" />
              {notificationCount.requestNotificationCount > 0 && (
                <NotificationBadge
                  className={css.notificationBadge}
                  count={notificationCount.requestNotificationCount}
                />
              )}
            </span>
          </NamedLink>
        </MenuItem>
        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="InboxPage" hideMenuItem={isCurrentUserAdmin}>
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('InboxPage'))}
            name="InboxPage"
          >
            <span className={css.menuItemBorder} />
            <span className={css.menuItemLabel}>
              <FormattedMessage id="TopbarDesktop.inbox" />
              {notificationCount.messageNotificationCount > 0 && (
                <NotificationBadge
                  className={css.notificationBadge}
                  count={notificationCount.messageNotificationCount}
                />
              )}
            </span>
          </NamedLink>
        </MenuItem>
        <MenuItem key="ManageListingsPage" hideMenuItem={!isCurrentUserAdmin}>
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
            name="ManageListingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.yourListingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="OrdersPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('OrdersPage'))}
            name="OrdersPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.ordersLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    currentSearchParams,
    notificationCount,
    currentUserCartItemsCount,
    searchProps,
    rootClassName,
    intl,
    location,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    topbarSearchFormPlaceholder,
    topbarListings,
    totalListingsCount,
    fetchTopbarListingsInProgress,
    fetchTopbarListingsError,
    initialSearchFormValues,
  } = props;

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;
  const isAuthenticationPage = AUTHENTICATION_PAGES.includes(currentPage);
  const isCurrentUserAdmin = isUserAdmin(currentUser);

  const currentUserOrderDraftItems =
    currentUser?.attributes?.profile?.protectedData?.orderDraftItems;
  const hasOrderDraftItems = currentUserOrderDraftItems && currentUserOrderDraftItems.length > 0;

  const classes = classNames(rootClassName || css.root, className);

  const signupLink = isAuthenticatedOrJustHydrated ? null : <SignupLink />;
  const loginLink = isAuthenticatedOrJustHydrated ? null : <LoginLink />;

  const createListingLink =
    isAuthenticatedOrJustHydrated && isCurrentUserAdmin ? <CreateListingLink /> : null;
  const inboxLink =
    isAuthenticatedOrJustHydrated && isCurrentUserAdmin ? (
      <InboxLink notificationCount={notificationCount.messageNotificationCount} />
    ) : null;

  const cartLink =
    isAuthenticatedOrJustHydrated && !isCurrentUserAdmin ? (
      <CartLink
        currentUserCartItemsCount={currentUserCartItemsCount}
        hasOrderDraftItems={hasOrderDraftItems}
        location={location}
      />
    ) : null;
  const searchLink = isAuthenticatedOrJustHydrated && !isCurrentUserAdmin ? <SearchLink /> : null;

  const topbarSearchFormMaybe =
    !isAuthenticatedOrJustHydrated || !isCurrentUserAdmin ? (
      <TopbarSearchForm
        className={css.searchForm}
        onSubmit={onSearchSubmit}
        initialValues={initialSearchFormValues}
        currentSearchParams={currentSearchParams}
        searchProps={searchProps}
        inputPlaceholder={topbarSearchFormPlaceholder}
        topbarListings={topbarListings}
        totalListingsCount={totalListingsCount}
        fetchTopbarListingsInProgress={fetchTopbarListingsInProgress}
        fetchTopbarListingsError={fetchTopbarListingsError}
      />
    ) : null;

  const hideCustomLinks = shouldHideCustomLinks(currentPage);
  const customLinksMenuMaybe =
    isAuthenticatedOrJustHydrated || hideCustomLinks ? null : (
      <CustomLinksMenu
        currentPage={currentPage}
        customLinks={customLinks}
        intl={intl}
        hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
      />
    );

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu
      currentPage={currentPage}
      currentUser={currentUser}
      isCurrentUserAdmin={isCurrentUserAdmin}
      onLogout={onLogout}
      notificationCount={notificationCount}
    />
  ) : null;

  if (isAuthenticationPage) {
    const isSignupPage = location?.pathname?.includes('signup');
    const isAdminPage = location?.pathname?.includes('admin');
    const pageType = isSignupPage ? 'Signup' : 'Login';
    const userType = isAdminPage ? 'Customer' : 'Admin';
    const linkName = `${pageType}${isAdminPage ? 'Page' : 'AdminPage'}`;
    const messageId = `TopbarDesktop.${pageType.toLowerCase()}As${userType}`;

    const signupOrLoginLink = (
      <NamedLink name={linkName} className={css.topbarLink}>
        <span className={css.topbarLinkLabel}>
          <FormattedMessage id={messageId} />
        </span>
      </NamedLink>
    );

    return (
      <nav className={classes}>
        <LinkedLogo
          className={css.logoLink}
          layout="desktop"
          alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
          linkToExternalSite={config?.topbar?.logoLink}
        />
        {signupOrLoginLink}
      </nav>
    );
  }

  return (
    <nav className={classes}>
      <div className={css.logoSearchAndCustomLinks}>
        <LinkedLogo
          className={css.logoLink}
          layout="desktop"
          alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
          linkToExternalSite={config?.topbar?.logoLink}
        />

        {/* Customer links */}
        {topbarSearchFormMaybe}
        {customLinksMenuMaybe}
      </div>

      <div className={css.independentLinks}>
        {/* Admin links */}
        {createListingLink}
        {inboxLink}

        {/* Customer links */}
        {cartLink}
        {searchLink}

        {/* Mutal links */}
        {signupLink}
        {loginLink}
        {profileMenuMaybe}
      </div>
    </nav>
  );
};

TopbarDesktop.defaultProps = {
  rootClassName: null,
  className: null,
  currentUser: null,
  currentPage: null,
  notificationCount: {
    messageNotificationCount: 0,
    requestNotificationCount: 0,
  },
  currentUserCartItemsCount: 0,
  initialSearchFormValues: {},
  topbarSearchFormPlaceholder: null,
  fetchTopbarListingsInProgress: false,
  fetchTopbarListingsError: null,
  config: null,
};

TopbarDesktop.propTypes = {
  rootClassName: string,
  className: string,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  isAuthenticated: bool.isRequired,
  onLogout: func.isRequired,
  notificationCount: shape({
    messageNotificationCount: number,
    requestNotificationCount: number,
  }),
  currentUserCartItemsCount: number,
  onSearchSubmit: func.isRequired,
  searchProps: shape({
    name: oneOf(['ProfilePage', 'SearchPage']).isRequired,
    params: shape({
      id: string,
    }).isRequired,
  }),
  topbarSearchFormPlaceholder: string,
  topbarListings: arrayOf(propTypes.listing),
  fetchTopbarListingsInProgress: bool.isRequired,
  fetchTopbarListingsError: propTypes.error,
  initialSearchFormValues: object,
  intl: intlShape.isRequired,
  config: object,
};

export default TopbarDesktop;
